import { ccv, crv, cva } from '@/styled-system/css';

export const toggleButtonGroupRecipe = cva({
  base: {
    'display': 'flex',
    'position': 'relative',
    'zIndex': 1,

    '& > :where(a, button)': {
      'position': 'relative',
      'zIndex': 1,
      '&[aria-pressed="true"]': {
        zIndex: 2,
      },
      '&:focus': {
        zIndex: 3,
      },
    },
  },

  variants: {
    conjoined: {
      true: {
        '& > :where(a, button)': {
          '&:not(:first-child):not(:last-child)': {
            borderRadius: 'none',
          },
        },
      },
      false: {},
    },

    ...crv('fluid', {
      true: {
        '& > :where(a, button)': {
          flexBasis: '100%',
          flexGrow: 1,
        },
      },
      false: {
        '& > :where(a, button)': {
          flexBasis: 'auto',
          flexGrow: 0,
        },
      },
    }),

    ...crv('orientation', {
      horizontal: {
        alignItems: 'start',
      },
      vertical: {
        alignItems: 'stretch',
      },
    }),

    ...crv('reverse', {
      true: {},
      false: {},
    }),
  },
  compoundVariants: [
    ...ccv({
      orientation: 'horizontal',
      reverse: true,
      css: {
        flexDirection: 'row-reverse',
      },
    }),

    ...ccv({
      orientation: 'horizontal',
      reverse: false,
      css: {
        flexDirection: 'row',
      },
    }),

    ...ccv({
      orientation: 'vertical',
      reverse: true,
      css: {
        flexDirection: 'column-reverse',
      },
    }),

    ...ccv({
      orientation: 'vertical',
      reverse: false,
      css: {
        flexDirection: 'column',
      },
    }),

    ...ccv({
      conjoined: false,
      orientation: 'horizontal',
      css: {
        gap: 'tighter',
      },
    }),

    ...ccv({
      conjoined: false,
      orientation: 'vertical',
      css: {
        gap: 'ButtonGroup.gap.vert',
      },
    }),

    /*
     * Each conjoined variant fully specifies all four corner radii and resets
     * off-axis margins. This prevents the base orientation/reverse styles from
     * leaking into other breakpoints when `orientation` or `reverse` are
     * responsive (the squared corners from one breakpoint would otherwise
     * persist into another and corrupt the radii).
     */
    ...ccv({
      conjoined: true,
      orientation: 'horizontal',
      reverse: false,
      css: {
        '& > :where(a, button)': {
          '&:not(:first-child)': {
            marginBlockStart: 0,
            marginBlockEnd: 0,
            marginInlineEnd: 0,
            marginInlineStart: 'conjoined-offsetBorder',
          },
          '&:first-child:not(:only-child)': {
            borderTopLeftRadius: 'obj.default',
            borderBottomLeftRadius: 'obj.default',
            borderTopRightRadius: 'none',
            borderBottomRightRadius: 'none',
          },
          '&:last-child:not(:only-child)': {
            borderTopRightRadius: 'obj.default',
            borderBottomRightRadius: 'obj.default',
            borderTopLeftRadius: 'none',
            borderBottomLeftRadius: 'none',
          },
        },
      },
    }),

    ...ccv({
      conjoined: true,
      orientation: 'horizontal',
      reverse: true,
      css: {
        '& > :where(a, button)': {
          '&:not(:first-child)': {
            marginBlockStart: 0,
            marginBlockEnd: 0,
            marginInlineStart: 0,
            marginInlineEnd: 'conjoined-offsetBorder',
          },
          '&:first-child:not(:only-child)': {
            borderTopRightRadius: 'obj.default',
            borderBottomRightRadius: 'obj.default',
            borderTopLeftRadius: 'none',
            borderBottomLeftRadius: 'none',
          },
          '&:last-child:not(:only-child)': {
            borderTopLeftRadius: 'obj.default',
            borderBottomLeftRadius: 'obj.default',
            borderTopRightRadius: 'none',
            borderBottomRightRadius: 'none',
          },
        },
      },
    }),

    ...ccv({
      conjoined: true,
      orientation: 'vertical',
      reverse: false,
      css: {
        '& > :where(a, button)': {
          '&:not(:first-child)': {
            marginInlineStart: 0,
            marginInlineEnd: 0,
            marginBlockEnd: 0,
            marginBlockStart: 'conjoined-offsetBorder',
          },
          '&:first-child:not(:only-child)': {
            borderTopLeftRadius: 'obj.default',
            borderTopRightRadius: 'obj.default',
            borderBottomLeftRadius: 'none',
            borderBottomRightRadius: 'none',
          },
          '&:last-child:not(:only-child)': {
            borderBottomLeftRadius: 'obj.default',
            borderBottomRightRadius: 'obj.default',
            borderTopLeftRadius: 'none',
            borderTopRightRadius: 'none',
          },
        },
      },
    }),

    ...ccv({
      conjoined: true,
      orientation: 'vertical',
      reverse: true,
      css: {
        '& > :where(a, button)': {
          '&:not(:first-child)': {
            marginInlineStart: 0,
            marginInlineEnd: 0,
            marginBlockStart: 0,
            marginBlockEnd: 'conjoined-offsetBorder',
          },
          '&:first-child:not(:only-child)': {
            borderBottomLeftRadius: 'obj.default',
            borderBottomRightRadius: 'obj.default',
            borderTopLeftRadius: 'none',
            borderTopRightRadius: 'none',
          },
          '&:last-child:not(:only-child)': {
            borderTopLeftRadius: 'obj.default',
            borderTopRightRadius: 'obj.default',
            borderBottomLeftRadius: 'none',
            borderBottomRightRadius: 'none',
          },
        },
      },
    }),
  ],
});
